import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { api, fmtUsd, clearAuthToken, downloadCsv } from "@/lib/journal";
import { MetricCards } from "@/components/MetricCards";
import { EquityCurve } from "@/components/EquityCurve";
import { AllocationChart } from "@/components/AllocationChart";
import { WhyAnalytics } from "@/components/WhyAnalytics";
import { TradesTable } from "@/components/TradesTable";
import { BuyDialog } from "@/components/BuyDialog";
import { SellDialog } from "@/components/SellDialog";
import { PortfolioBar } from "@/components/PortfolioBar";
import { ComparePanel } from "@/components/ComparePanel";
import { BasketPanel } from "@/components/BasketPanel";
import { EditNotesDialog } from "@/components/EditNotesDialog";
import { CurrencySwitcher } from "@/components/CurrencySwitcher";
import { AlertHistoryDialog } from "@/components/AlertHistoryDialog";
import { SettingsDialog } from "@/components/SettingsDialog";
import { MergeDialog } from "@/components/MergeDialog";
import { MarkPriceDialog } from "@/components/MarkPriceDialog";
import { WalletImportDialog } from "@/components/WalletImportDialog";
import { CsvImportDialog } from "@/components/CsvImportDialog";
import { Plus, ArrowsClockwise, DownloadSimple, FileCsv, FileArrowUp, CloudArrowDown, StackSimple, Gear, ChartBar, BellSimple, SignOut } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

export default function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [portfolios, setPortfolios] = useState([]);
  const [activeId, setActiveId] = useState("all");
  const [trades, setTrades] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [whyStats, setWhyStats] = useState(null);
  const [alloc, setAlloc] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [assetFilter, setAssetFilter] = useState("all");
  const [buyOpen, setBuyOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [markTrade, setMarkTrade] = useState(null);
  const [editTrade, setEditTrade] = useState(null);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [fxTick, setFxTick] = useState(0);
  const bumpFx = useCallback(() => setFxTick((t) => t + 1), []);
  const [sellTrade, setSellTrade] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const scope = activeId === "all" ? {} : { portfolio_id: activeId };
      const [p, t, a, al, w] = await Promise.all([
        api.get("/portfolios"),
        api.get("/trades", { params: scope }),
        api.get("/analytics", { params: scope }),
        api.get("/analytics/allocation", { params: scope }),
        api.get("/analytics/why", { params: scope }),
      ]);
      setPortfolios(p.data.portfolios);
      setTrades(t.data);
      setAnalytics(a.data);
      setAlloc(al.data);
      setWhyStats(w.data);
    } catch (e) {
      toast.error(t("dashboard.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [activeId, t]);

  useEffect(() => { load(); }, [load]);

  const portfolioMap = useMemo(
    () => Object.fromEntries(portfolios.map((p) => [p.id, p.name])),
    [portfolios]
  );

  const defaultPortfolioId = useMemo(
    () => (activeId !== "all" ? activeId : portfolios[0]?.id),
    [activeId, portfolios]
  );

  const activeName = activeId === "all" ? t("dashboard.allPortfolios") : portfolioMap[activeId] || t("dashboard.portfolio");
  const activePortfolio = portfolios.find((p) => p.id === activeId);

  const assetOptions = useMemo(
    () => [...new Set(trades.map((t) => t.asset).filter(Boolean))].sort(),
    [trades]
  );

  const filtered = useMemo(
    () => trades.filter(
      (t) => (statusFilter === "all" || t.status === statusFilter) &&
             (assetFilter === "all" || t.asset === assetFilter)
    ),
    [trades, statusFilter, assetFilter]
  );

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/wallet/sync", null, {
        params: activeId === "all" ? {} : { portfolio_id: activeId },
      });
      const added = data.results?.reduce((n, r) => n + r.imported, 0) ?? 0;
      const sells = data.results?.reduce((n, r) => n + (r.sells_applied || 0), 0) ?? 0;
      const errs = (data.results || []).filter((r) => r.error);
      if (!data.results?.length) toast.info(t("dashboard.syncNoWallet"));
      else if (added || sells)
        toast.success(t("dashboard.syncDone", { count: added }));
      else toast.success(errs.length ? t("dashboard.syncIssues", { count: errs.length }) : t("dashboard.syncNoNew"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("dashboard.syncFailed"));
    } finally {
      setSyncing(false);
    }
  };

  const exportCsv = async () => {
    try {
      await downloadCsv(
        "/trades/export",
        activeId === "all" ? {} : { portfolio_id: activeId },
        `tj13-trades-${new Date().toISOString().slice(0, 10)}.csv`
      );
    } catch {
      toast.error(t("dashboard.exportFailed"));
    }
  };

  const remove = async (id) => {    try {
      await api.delete(`/trades/${id}`);
      toast.success(t("dashboard.tradeRemoved"));
      load();
    } catch {
      toast.error(t("dashboard.deleteFailed"));
    }
  };

  return (
    <div className="grain min-h-screen bg-[#050505]">
      <Toaster position="top-right" theme="dark" />

      <header className="sticky top-0 z-20 border-b border-[#1F1F1F] bg-black/60 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-y-3 px-4 py-4 md:px-8">
          <div className="flex min-w-0 items-center gap-3">
            <div className="h-6 w-[3px] shrink-0 bg-[#C58361]" />
            <div className="min-w-0">
              <h1 className="whitespace-nowrap font-display text-base font-medium tracking-tight text-white md:text-lg">
                TJ13{" "}
                <span className="text-[#C58361]">
                  by{" "}
                  <a
                    data-testid="alvo13-link"
                    href="https://alvo-13.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline decoration-[#C58361]/40 decoration-1 underline-offset-4 transition-colors hover:text-[#E0A27F] hover:decoration-[#E0A27F]"
                  >
                    ALVO 13
                  </a>
                </span>
              </h1>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <CurrencySwitcher onChanged={bumpFx} />
            <Button
              data-testid="refresh-btn"
              onClick={load}
              className="h-9 rounded-none border border-[#1F1F1F] bg-transparent px-3 text-zinc-400 hover:bg-[#111111] hover:text-white"
            >
              <ArrowsClockwise size={15} />
            </Button>
            <Button
              data-testid="sync-now-btn"
              onClick={syncNow}
              disabled={syncing}
              className="h-9 rounded-none border border-[#1F1F1F] bg-transparent px-3 text-xs text-white hover:bg-[#111111]"
            >
              <CloudArrowDown size={15} className={`mr-2 ${syncing ? "animate-pulse" : ""}`} />
              <span className="hidden sm:inline">{syncing ? t("header.syncing") : t("header.syncNow")}</span>
            </Button>
            <Button
              data-testid="csv-import-open-btn"
              onClick={() => setCsvOpen(true)}
              className="h-9 rounded-none border border-[#1F1F1F] bg-transparent px-3 text-xs text-white hover:bg-[#111111]"
            >
              <FileArrowUp size={15} className="mr-2" />
              <span className="hidden sm:inline">{t("header.importCsv")}</span>
            </Button>
            <Button
              data-testid="export-csv-btn"
              onClick={exportCsv}
              className="h-9 rounded-none border border-[#1F1F1F] bg-transparent px-3 text-xs text-white hover:bg-[#111111]"
            >
              <FileCsv size={15} className="mr-2" />
              <span className="hidden sm:inline">{t("header.exportCsv")}</span>
            </Button>
            <Button
              data-testid="yearly-review-btn"
              onClick={() => navigate("/review")}
              className="h-9 rounded-none border border-[#1F1F1F] bg-transparent px-3 text-xs text-white hover:bg-[#111111]"
            >
              <ChartBar size={15} className="mr-2" />
              <span className="hidden lg:inline">{t("header.review")}</span>
            </Button>
            <Button
              data-testid="merge-positions-btn"
              onClick={() => setMergeOpen(true)}
              className="h-9 rounded-none border border-[#1F1F1F] bg-transparent px-3 text-xs text-white hover:bg-[#111111]"
            >
              <StackSimple size={15} className="mr-2" />
              <span className="hidden lg:inline">{t("header.merge")}</span>
            </Button>
            <Button
              data-testid="alert-history-btn"
              onClick={() => setAlertsOpen(true)}
              className="h-9 rounded-none border border-[#1F1F1F] bg-transparent px-3 text-zinc-400 hover:bg-[#111111] hover:text-white"
            >
              <BellSimple size={15} />
            </Button>
            <Button
              data-testid="settings-btn"
              onClick={() => setSettingsOpen(true)}
              className="h-9 rounded-none border border-[#1F1F1F] bg-transparent px-3 text-zinc-400 hover:bg-[#111111] hover:text-white"
            >
              <Gear size={15} />
            </Button>
            <Button
              data-testid="wallet-import-btn"
              onClick={() => setImportOpen(true)}
              className="h-9 rounded-none border border-[#1F1F1F] bg-transparent px-3 text-xs text-white hover:bg-[#111111]"
            >
              <DownloadSimple size={15} className="mr-2" />
              <span className="hidden sm:inline">{t("header.importWallet")}</span>
            </Button>
            <Button
              data-testid="new-trade-btn"
              onClick={() => setBuyOpen(true)}
              className="h-9 rounded-none bg-[#C58361] px-4 font-medium text-black hover:bg-[#A86B4C]"
            >
              <Plus size={15} className="mr-2" /> {t("header.newEntry")}
            </Button>
            <Button
              data-testid="logout-btn"
              title={t("header.logout")}
              onClick={() => {
                clearAuthToken();
                window.dispatchEvent(new Event("tj13-unauthorized"));
              }}
              className="h-9 rounded-none border border-[#1F1F1F] bg-transparent px-3 text-zinc-400 hover:bg-[#111111] hover:text-white"
            >
              <SignOut size={15} />
            </Button>
          </div>
        </div>
      </header>

      <main key={fxTick} className="relative z-[2] mx-auto max-w-7xl px-4 py-10 md:px-8 md:py-12">
        <div className="mb-8">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#C58361]">{t("dashboard.eyebrow")}</p>
          <h2 className="mt-3 font-display text-3xl font-medium tracking-tighter text-white sm:text-4xl">
            {activeName}
          </h2>
        </div>

        <div className="mb-10">
          <PortfolioBar
            portfolios={portfolios}
            activeId={activeId}
            setActiveId={setActiveId}
            onChanged={load}
          />
        </div>

        {loading ? (
          <p className="font-mono text-sm text-zinc-600">{t("common.loading")}</p>
        ) : activePortfolio?.kind === "basket" ? (
          <BasketPanel portfolio={activePortfolio} onChanged={load} />
        ) : (
          <div className="space-y-10">
            <MetricCards a={analytics} />
            <div className="grid gap-4 lg:grid-cols-3">
              <div className="lg:col-span-2">
                <EquityCurve data={analytics?.equity_curve} portfolioId={activeId} />
              </div>
              <div className="border border-[#1F1F1F] bg-surface p-6" data-testid="stats-panel">
                <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{t("dashboard.fundStats")}</h3>
                <dl className="mt-6 space-y-4 font-mono text-sm">
                  {[
                    [t("dashboard.openPositions"), analytics?.open_trades ?? 0],
                    [t("dashboard.closedPositions"), analytics?.closed_trades ?? 0],
                    [t("dashboard.winRate"), `${analytics?.win_rate ?? 0}%`],
                    [t("dashboard.bestTrade"), fmtUsd(analytics?.best_trade ?? 0)],
                    [t("dashboard.worstTrade"), fmtUsd(analytics?.worst_trade ?? 0)],
                  ].map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between border-b border-[#1F1F1F] pb-3">
                      <dt className="text-zinc-500">{k}</dt>
                      <dd className="text-white">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>

            <WhyAnalytics data={whyStats} />
            {activeId === "all" && <ComparePanel portfolios={portfolios} />}
            <AllocationChart data={alloc} />

            <TradesTable
              trades={filtered}
              prices={analytics?.prices}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              assetFilter={assetFilter}
              setAssetFilter={setAssetFilter}
              assetOptions={assetOptions}
              onSell={setSellTrade}
              onDelete={remove}
              portfolioMap={portfolioMap}
              showPortfolio={activeId === "all"}
              onSetMark={setMarkTrade}
              onEditNotes={setEditTrade}
            />
          </div>
        )}
      </main>

      <BuyDialog
        open={buyOpen}
        onOpenChange={setBuyOpen}
        onSaved={load}
        portfolios={portfolios}
        defaultPortfolioId={defaultPortfolioId}
      />
      <SellDialog trade={sellTrade} onOpenChange={() => setSellTrade(null)} onSaved={load} />
      <WalletImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        portfolios={portfolios}
        defaultPortfolioId={defaultPortfolioId}
        onImported={load}
      />
      <CsvImportDialog
        open={csvOpen}
        onOpenChange={setCsvOpen}
        portfolios={portfolios}
        defaultPortfolioId={defaultPortfolioId}
        onImported={load}
      />
      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
      <MergeDialog
        open={mergeOpen}
        onOpenChange={setMergeOpen}
        portfolioId={activeId}
        onMerged={load}
      />
      <MarkPriceDialog trade={markTrade} onOpenChange={() => setMarkTrade(null)} onSaved={load} />
      <EditNotesDialog trade={editTrade} onOpenChange={() => setEditTrade(null)} onSaved={load} portfolios={portfolios} />
      <AlertHistoryDialog open={alertsOpen} onOpenChange={setAlertsOpen} />
    </div>
  );
}
